import React, { useEffect, useState, useCallback, useRef } from 'react';
import { TopActionBar } from '../../components';
import { ModuleBridge } from '../../components';
import { MigrationAppliancesTable } from './MigrationAppliancesTable';
import { MIGRATION_LINKS, MIGRATION_LABELS, MIGRATION_BUTTON_TEXT } from '../../constants/docLinks';
import { Actions } from './actions';
import BulkAddTagsModal from '../../components/modals/BulkAddTagsModal';
import CloudAccountModal from '../../components/modals/CloudAccountModal';
import { DOC_TYPES } from '../../constants/docLinks';

export const Clouds = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [addTagsModalOpen, setAddTagsModalOpen] = useState(false);
    const [selectedAppliancesForTags, setSelectedAppliancesForTags] = useState([]);
    const [cloudAccountModalOpen, setCloudAccountModalOpen] = useState(false);
    const [editingCloudAccountId, setEditingCloudAccountId] = useState(null);
    const bulkDeleteTriggerRef = useRef(null);
    const bulkAddTagsTriggerRef = useRef(null);
    const refetchDataRef = useRef(null);
    const editCloudAccountTriggerRef = useRef(null);

    const userOrg = sessionStorage.getItem('userOrg');

    // We need to call this to delete any Backbone polling instances
    // when we change routes.
    // Delete this code when the Migration Appliances page is not using any Backbone modules
    useEffect(() => {
        return () => {
            if (window?.clearMigrationAppliancePolling) {
                window.clearMigrationAppliancePolling();
            }
        };
    }, []);

    const handleBulkDelete = useCallback((applianceIds) => {
        // Trigger the bulk delete modal in MigrationAppliancesTable
        if (bulkDeleteTriggerRef.current) {
            bulkDeleteTriggerRef.current(applianceIds);
        }
    }, []);

    const handleBulkAddTags = useCallback((applianceIds) => {
        setSelectedAppliancesForTags(applianceIds);
        setAddTagsModalOpen(true);
    }, []);

    const handleBulkActionSuccess = useCallback(() => {
        // Clear selected rows after successful bulk action
        setSelectedRowKeys([]);
        // Refresh table data to show updated tags/move groups
        if (refetchDataRef.current) {
            refetchDataRef.current();
        }
    }, []);

    const handleAddTagsModalClose = useCallback(() => {
        setAddTagsModalOpen(false);
        setSelectedAppliancesForTags([]);
    }, []);

    const handleDataRefreshNeeded = useCallback((refetchFunction) => {
        refetchDataRef.current = refetchFunction;
    }, []);

    const handleAddCloudAccount = useCallback(() => {
        setEditingCloudAccountId(null);
        setCloudAccountModalOpen(true);
    }, [cloudAccountModalOpen]);

    const handleEditCloudAccount = useCallback((cloudAccountId) => {
        setEditingCloudAccountId(cloudAccountId);
        setCloudAccountModalOpen(true);
    }, []);

    const handleCloudAccountModalClose = useCallback(() => {
        setCloudAccountModalOpen(false);
        setEditingCloudAccountId(null);
    }, []);

    const handleCloudAccountSuccess = useCallback(
        (cloudAccount, setupAppliance) => {
            if (refetchDataRef.current) {
                refetchDataRef.current();
            }

            // If setupAppliance is true and this is a new account, trigger appliance setup
            if (setupAppliance && cloudAccount && !editingCloudAccountId) {
                // Trigger legacy appliance setup flow
                setTimeout(() => {
                    if (window.setupAppliance) {
                        window.setupAppliance(cloudAccount.id);
                    }
                }, 500);
            }
        },
        [editingCloudAccountId],
    );

    useEffect(() => {
        editCloudAccountTriggerRef.current = handleEditCloudAccount;
    }, [handleEditCloudAccount]);

    return (
        <div className="migration-appliances">
            <TopActionBar showDocumentationLinksDropdown={true} documentationType={DOC_TYPES.MIGRATION}>
                <Actions
                    selectedRowKeys={selectedRowKeys}
                    onDelete={handleBulkDelete}
                    onAddTags={handleBulkAddTags}
                    onAddCloudAccount={handleAddCloudAccount}
                />
            </TopActionBar>

            {/* Comment out these lines to use the new React table and logic */}
            {/*<div id="clouds_container"></div>*/}
            {/*<ModuleBridge moduleId="cloud_list_module" />*/}

            {/* Uncomment these lines to use the new React table and logic that lets us use the
               Add Migration Appliance modal in Backbone with the rest of the page in React
            */}
            <MigrationAppliancesTable
                selectedRowKeys={selectedRowKeys}
                onSelectedRowKeysChange={setSelectedRowKeys}
                bulkDeleteTriggerRef={bulkDeleteTriggerRef}
                bulkAddTagsTriggerRef={bulkAddTagsTriggerRef}
                editCloudAccountTriggerRef={editCloudAccountTriggerRef}
                onDataRefreshNeeded={handleDataRefreshNeeded}
                onBulkActionSuccess={handleBulkActionSuccess}
            />
            <ModuleBridge moduleId="cloud_list_module_react" />

            <BulkAddTagsModal
                open={addTagsModalOpen}
                onClose={handleAddTagsModalClose}
                selectedResources={selectedAppliancesForTags}
                userOrg={userOrg}
                onSuccess={handleBulkActionSuccess}
                title="Add Tags to Selected Migration Appliance(s)"
                resourceType="migration appliance"
                apiConfig={{
                    getResourceEndpoint: (resourceId) => `/api/v3/cloudaccounts/${resourceId}`,
                    updateResourceEndpoint: (resourceId) => `/api/v3/cloudaccounts/${resourceId}`,
                }}
            />

            <CloudAccountModal
                open={cloudAccountModalOpen}
                onClose={handleCloudAccountModalClose}
                onSuccess={handleCloudAccountSuccess}
                cloudAccountId={editingCloudAccountId}
                userOrg={userOrg}
            />
        </div>
    );
};
